/**
 * Focus trap
 * Keeps Tab (and Shift+Tab) cycling inside an open overlay, so keyboard and
 * screen-reader users cannot land in the page hidden behind it.
 *
 * @package StandOut
 * @since 2.0.0
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';
/**
 * Trap focus inside a container while it is open.
 *
 * The focusable list is collected per keypress, so content swapped in or out
 * while the trap is active (accordion groups, AJAX results) stays covered.
 *
 * @param container - The overlay to contain focus in
 * @returns Release function — call it when the overlay closes
 */
export const trapFocus = (container) => {
    const onKeydown = (event) => {
        if (event.key !== "Tab") {
            return;
        }
        // getClientRects filters hidden elements; offsetParent would also drop
        // the drawer's own position:fixed descendants.
        const focusable = Array.from(container.querySelectorAll(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
        if (focusable.length === 0) {
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        if (!(active instanceof HTMLElement) || !container.contains(active)) {
            event.preventDefault();
            first.focus();
            return;
        }
        if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    };
    document.addEventListener("keydown", onKeydown, true);
    return () => {
        document.removeEventListener("keydown", onKeydown, true);
    };
};
